import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Sparkles, Clock, LogOut, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * BranchGate — mandatory one-time community-branch picker shown right after
 * OTP verification. Only "hindu" opens the full platform. Every other choice
 * locks the user on a fullscreen "Coming Soon" screen (no back button) —
 * we'll unlock those branches when we officially launch them.
 *
 * Placement: wraps ProtectedRoute's children so the gate blocks every
 * private route until the branch is set + supported.
 */

const BRANCH_META = {
    hindu:        { title: "Hindu",           tagline: "Family, dharma & vansh — India's largest heritage tradition",           gradient: "from-[#EA580C] via-[#F97316] to-[#FBBF24]", icon: "🕉️", supported: true },
    jain:         { title: "Jain",            tagline: "Ahimsa, discipline & the eternal Tirthankar lineage",                    gradient: "from-[#B91C1C] via-[#DC2626] to-[#EAB308]", icon: "🪷", supported: false },
    sikh_punjabi: { title: "Sikh / Punjabi",  tagline: "Ek Onkar — Guru-led legacy, warrior spirit & Punjabi soul",              gradient: "from-[#F59E0B] via-[#FBBF24] to-[#65A30D]", icon: "☬",  supported: false },
    sindhi:       { title: "Sindhi",          tagline: "Trade, resilience & Jhulelal — a proud diaspora heritage",               gradient: "from-[#0F766E] via-[#14B8A6] to-[#22D3EE]", icon: "🕉",  supported: false },
    other:        { title: "Other Community", tagline: "Christian, Muslim, Buddhist, Parsi, tribal & more — coming as we grow", gradient: "from-[#4F46E5] via-[#6366F1] to-[#A855F7]", icon: "🌏", supported: false },
};
const BRANCH_ORDER = ["hindu", "jain", "sikh_punjabi", "sindhi", "other"];

export default function BranchGate({ children }) {
    const { user, refresh, logout } = useAuth();
    const [status, setStatus] = useState("loading");    // "loading" | "picker" | "supported" | "coming_soon" | "error"
    const [selected, setSelected] = useState(null);      // branch key when the user is on coming-soon
    const [busy, setBusy] = useState(false);

    const loadBranch = useCallback(async () => {
        try {
            const { data } = await api.get("/me/branch");
            if (!data.branch) { setStatus("picker"); return; }
            setSelected(data.branch);
            setStatus(data.supported ? "supported" : "coming_soon");
        } catch (e) {
            setStatus("error");
        }
    }, []);

    useEffect(() => {
        if (!user) return;
        // If UserOut already carries the branch, trust it — no round-trip.
        if (user.branch) {
            const meta = BRANCH_META[user.branch];
            setSelected(user.branch);
            setStatus(meta?.supported ? "supported" : "coming_soon");
        } else {
            loadBranch();
        }
    }, [user, loadBranch]);

    const chooseBranch = async (key) => {
        setBusy(true);
        try {
            const { data } = await api.post("/me/branch", { branch: key });
            setSelected(key);
            setStatus(data.supported ? "supported" : "coming_soon");
            await refresh();
            if (data.supported) {
                toast.success(`Welcome — the ${data.label} branch is ready for you.`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save your choice. Try again.");
        } finally {
            setBusy(false);
        }
    };

    if (status === "loading") {
        return (
            <div data-testid="branch-gate-loading" className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#FEF3C7] to-white">
                <div className="text-center text-[#6D28D9]"><Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" /><div className="font-medium">Preparing your family space…</div></div>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white p-4">
                <div className="text-center">
                    <p className="text-red-700 font-semibold mb-2">Could not load your community preference.</p>
                    <button onClick={loadBranch} className="text-[#6D28D9] font-semibold underline">Retry</button>
                </div>
            </div>
        );
    }

    if (status === "picker") {
        return (
            <div data-testid="branch-gate-picker"
                className="fixed inset-0 z-[80] min-h-screen overflow-y-auto bg-gradient-to-b from-[#FFF7ED] via-[#FEF3C7] to-[#FDF2F8]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 border border-[#EA580C]/25 backdrop-blur-sm shadow-sm mb-3">
                            <Sparkles className="h-3.5 w-3.5 text-[#EA580C]" />
                            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#9A3412]">One-time setup</span>
                        </div>
                        <h1 className="font-extrabold text-[#4C1D95] leading-[1.1]" style={{ fontSize: "clamp(24px, 4vw, 40px)" }}>
                            Which family tradition should we prepare for you?
                        </h1>
                        <p className="mt-2.5 text-[13.5px] sm:text-[15px] text-[#4B5563] max-w-2xl mx-auto">
                            Pick your community once — we'll tailor the sanskaar, festivals, ceremonies and stories accordingly. This can't be changed later without contacting support.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="branch-gate-options">
                        {BRANCH_ORDER.map((key) => {
                            const b = BRANCH_META[key];
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    disabled={busy}
                                    onClick={() => chooseBranch(key)}
                                    data-testid={`branch-gate-choice-${key}`}
                                    className="group relative text-left rounded-2xl overflow-hidden bg-white ring-1 ring-black/5 shadow-[0_10px_30px_rgba(76,29,149,0.10)] hover:shadow-[0_20px_50px_rgba(76,29,149,0.22)] transition-all duration-300 hover:-translate-y-1 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <div className={`h-24 bg-gradient-to-br ${b.gradient} relative flex items-center justify-center`}>
                                        <span className="text-5xl drop-shadow-md" aria-hidden="true">{b.icon}</span>
                                        {!b.supported && (
                                            <span className="absolute top-2 right-2 text-[9.5px] font-bold uppercase tracking-wider bg-white/95 text-[#4C1D95] rounded-full px-2 py-0.5 shadow-sm">
                                                <Clock className="h-2.5 w-2.5 inline mr-0.5 -mt-0.5" /> Coming soon
                                            </span>
                                        )}
                                        {b.supported && (
                                            <span className="absolute top-2 right-2 text-[9.5px] font-bold uppercase tracking-wider bg-[#065F46] text-white rounded-full px-2 py-0.5 shadow-sm">
                                                <CheckCircle2 className="h-2.5 w-2.5 inline mr-0.5 -mt-0.5" /> Available now
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <div className="text-[15.5px] font-extrabold text-[#1F2937] group-hover:text-[#6D28D9] transition-colors">{b.title}</div>
                                        <p className="mt-1 text-[12.5px] text-[#4B5563] leading-snug">{b.tagline}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <p className="mt-8 text-center text-[11.5px] text-[#6B7280]">
                        Your choice is stored privately and used only to personalise your family repository experience.
                    </p>
                </div>
            </div>
        );
    }

    if (status === "coming_soon") {
        const b = BRANCH_META[selected] || BRANCH_META.other;
        return (
            <div data-testid="branch-gate-coming-soon"
                className="fixed inset-0 z-[80] min-h-screen overflow-y-auto bg-gradient-to-b from-[#0B1E4A] via-[#1E1B4B] to-[#312E81] text-white">
                <div className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-10 relative">
                    <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-[70%] rounded-full bg-[#F59E0B]/20 blur-3xl" />
                    <div className="pointer-events-none absolute -bottom-24 right-0 h-56 w-[50%] rounded-full bg-[#A855F7]/15 blur-3xl" />

                    <AnimatePresence>
                        <motion.div
                            key={selected}
                            initial={{ opacity: 0, y: 16, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                            className="relative max-w-2xl w-full text-center"
                        >
                            <div className={`inline-flex h-24 w-24 sm:h-28 sm:w-28 items-center justify-center rounded-full bg-gradient-to-br ${b.gradient} shadow-[0_20px_60px_rgba(245,158,11,0.35)] text-6xl sm:text-7xl mb-6`}>
                                <span aria-hidden="true">{b.icon}</span>
                            </div>
                            <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1.5 rounded-full bg-white/8 border border-[#F59E0B]/40 backdrop-blur-sm">
                                <Clock className="h-3.5 w-3.5 text-[#F59E0B]" />
                                <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#F59E0B]">Coming Soon</span>
                            </div>
                            <h1 className="font-extrabold leading-[1.08] mb-3" style={{ fontSize: "clamp(28px, 5vw, 48px)" }}>
                                The <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">{b.title}</span> branch<br />is being prepared for you
                            </h1>
                            <p className="text-[14px] sm:text-[16px] text-white/80 leading-relaxed max-w-lg mx-auto mb-6">
                                {b.tagline}. Our researchers &amp; community elders are carefully building this experience so every ritual, festival and lineage record does justice to your heritage.
                            </p>
                            <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-4 sm:p-5 mb-6 text-left" data-testid="branch-gate-notify-card">
                                <div className="flex items-start gap-3">
                                    <div className="h-8 w-8 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 flex items-center justify-center shrink-0">
                                        <Sparkles className="h-4 w-4 text-[#F59E0B]" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-semibold text-[13.5px] text-white">We've noted your interest</div>
                                        <p className="text-[12px] text-white/70 mt-1 leading-relaxed">
                                            We'll notify you on your registered number the moment the {b.title} branch goes live. No further action needed from your side.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => logout()}
                                data-testid="branch-gate-logout"
                                className="inline-flex items-center gap-2 text-[12.5px] text-white/70 hover:text-white transition-colors underline underline-offset-4"
                            >
                                <LogOut className="h-3.5 w-3.5" /> Sign out
                            </button>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        );
    }

    // status === "supported" → render the platform
    return <>{children}</>;
}
